import { useCallback, useEffect, useRef, useState } from 'react';
import Button from './ui/Button';
import { apiClient } from '../utils/apiClient';
import './Attachments.css';
import { confirmDialog } from '../utils/dialogs';

// Files panel for anything attachable - tickets, customers, invoices. The
// API is the one that decides who may see what (every route in
// attachments.routes.js is ownership-checked, not just authenticated), so
// this component only has to render whatever comes back.
//
// Usage: <Attachments entityType="ticket" entityId={ticket.ticket_id} />

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconFor = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'fa-file-image';
  if (ext === 'pdf') return 'fa-file-pdf';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'fa-file-word';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'fa-file-excel';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'fa-file-zipper';
  if (['txt', 'log', 'md'].includes(ext)) return 'fa-file-lines';
  return 'fa-file';
};

export default function Attachments({ entityType, entityId, title = 'Files' }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const rows = await apiClient.get(
        `/api/attachments?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`
      );
      setFiles(rows);
      setError('');
    } catch (err) {
      console.error('Error loading attachments:', err);
      setError(err.message || 'Could not load files.');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (chosen.length === 0) return;

    setBusy(true);
    setError('');
    try {
      for (const file of chosen) {
        // Checked here as well as server-side purely so someone picking a
        // 40MB video gets told immediately instead of after the upload.
        if (file.size > MAX_UPLOAD_BYTES) {
          throw new Error(`${file.name} is larger than 10MB.`);
        }
        const query = new URLSearchParams({
          entity_type: entityType,
          entity_id: String(entityId),
          filename: file.name,
          content_type: file.type || 'application/octet-stream'
        });
        await apiClient.upload(`/api/attachments?${query.toString()}`, file);
      }
      await load();
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError(err.message || 'Could not upload that file.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const get = async (file) => {
    try {
      await apiClient.download(`/api/attachments/${file.id}/download`, file.original_name);
    } catch (err) {
      setError(err.message || 'Could not download that file.');
    }
  };

  const remove = async (file) => {
    if (!await confirmDialog(`Delete ${file.original_name}? This can't be undone.`)) return;
    try {
      await apiClient.delete(`/api/attachments/${file.id}`);
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete that file.');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="attachments">
      <div className="attachments-header">
        <h3>{title}{files.length > 0 && <span className="attachments-count">{files.length}</span>}</h3>
        <Button variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading...' : 'Upload'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="attachments-input"
          onChange={(e) => uploadFiles(e.target.files)}
        />
      </div>

      {error && <div className="attachments-error">{error}</div>}

      <div
        className={`attachments-drop ${dragging ? 'is-dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {loading ? (
          <p className="attachments-empty">Loading files...</p>
        ) : files.length === 0 ? (
          <p className="attachments-empty">No files yet — drop one here, or use Upload.</p>
        ) : (
          <ul className="attachments-list">
            {files.map((file) => (
              <li key={file.id} className="attachments-item">
                <i className={`fas ${iconFor(file.original_name)} attachments-icon`}></i>
                <button type="button" className="attachments-name" onClick={() => get(file)} title="Download">
                  {file.original_name}
                </button>
                <span className="attachments-size">{formatSize(file.size_bytes)}</span>
                <span className="attachments-date">{new Date(file.createdAt).toLocaleDateString()}</span>
                <button
                  type="button"
                  className="attachments-remove"
                  onClick={() => remove(file)}
                  aria-label={`Delete ${file.original_name}`}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
